import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Package, ChevronLeft, ChevronRight } from 'lucide-react';

interface SkuItem {
  sku: string;
  product_count: number;
  first_product_title: string;
  first_product_image?: string | null;
  custom_name?: string | null;
  custom_image?: string | null;
  reference_price?: number | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SkuListColumnProps {
  skuList: SkuItem[];
  selectedSku: string | null;
  searchTerm: string;
  isLoading: boolean;
  pagination?: Pagination;
  onSearchChange: (value: string) => void;
  onSelectSku: (sku: string) => void;
  onCreateNewSku: () => void;
  onPageChange?: (page: number) => void;
}

const SkuListColumn: React.FC<SkuListColumnProps> = ({
  skuList,
  selectedSku,
  searchTerm,
  isLoading,
  pagination,
  onSearchChange,
  onSelectSku,
  onCreateNewSku,
  onPageChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Package className="h-4 w-4" />
          {t('skus.skus_header')}
          {pagination && pagination.total > 0 && (
            <span className="text-xs font-normal text-gray-500">
              ({pagination.total})
            </span>
          )}
        </h3>
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('skus.search_sku_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* SKU List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
          </div>
        ) : skuList.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchTerm ? t('skus.no_skus_found') : t('skus.no_skus_registered')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {skuList.map((item) => {
              const displayImage = item.custom_image || item.first_product_image;
              const displayName = item.custom_name || item.sku;
              const formatPrice = (price: number) => {
                return new Intl.NumberFormat('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(price);
              };

              return (
                <button
                  key={item.sku}
                  onClick={() => onSelectSku(item.sku)}
                  className={`
                    w-full p-3 text-left transition-colors flex gap-3
                    ${selectedSku === item.sku
                      ? 'bg-pink-50 border-l-4 border-pink-500'
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                    }
                  `}
                >
                  {/* Product Image */}
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded overflow-hidden">
                    {displayImage ? (
                      <img
                        src={displayImage}
                        alt=""
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  {/* SKU Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 break-words leading-snug" title={displayName}>
                      {displayName}
                    </div>
                    {item.custom_name && item.custom_name !== item.sku && (
                      <div className="text-[10px] text-gray-400 leading-tight">
                        SKU: {item.sku}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 leading-snug break-words line-clamp-1" title={item.first_product_title}>
                      {item.first_product_title}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-pink-600">
                        ({item.product_count} {item.product_count === 1 ? t('skus.product_singular') : t('skus.product_plural')})
                      </span>
                      {item.reference_price && (
                        <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                          {t('skus.ref')}: {formatPrice(item.reference_price)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {(() => {
            const start = (pagination.page - 1) * pagination.limit + 1;
            const end = pagination.total
              ? Math.min(pagination.page * pagination.limit, pagination.total)
              : pagination.page * pagination.limit;
            const total = pagination.total ?? '—';
            return (
              <span className="text-[11px] text-gray-500">
                {start} {t('skus.pagination.from')} {end} {t('skus.pagination.of')} {total} · {t('skus.pagination.page')} {pagination.page} {t('skus.pagination.of_pages')} {pagination.totalPages}
              </span>
            );
          })()}
          <button
            onClick={() => onPageChange?.(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => onPageChange?.(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create New SKU Button */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onCreateNewSku}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          {t('skus.create_new_sku')}
        </button>
      </div>
    </div>
  );
};

export default SkuListColumn;
